import {
  ConfirmationModal,
  DateRange,
  DateRangeInput,
  DATE_FORMATTER_STR,
  formatDate,
  NotApplicable,
  Select,
  Typography,
  useLoading,
  ValueOpt,
  StatsTeam,
  GameInfo,
  Header,
  Filters,
  useBreakpoints,
} from "best-common-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { deleteDayOfTicketRequest, getDayOfRequests } from "../../../api/RequesTixApi";
import AdminActiveTodaysTicketsTable from "../../../components/todaysTickets/admin/AdminActiveTodaysTicketsTable";
import RouteConstants, { urlReplace } from "../../../constants/RouteConstants";
import { useDropdowns } from "../../../contexts/DropdownsContext";
import { DayOfGameRequestDTO, DayOfRequestStatus } from "../../../types/TodaysTickets";
import { nextMonthStr, todayStr } from "../../../util/DateObjUtil";
import { useUrlParams } from "../../../util/RouteUtil";
import { getTeamOption } from "../../../util/TeamUtil";
import { formatSubmittedDate } from "../../../util/TimeUtil";
import TodaysTicketsDataTable, { DayOfTableData } from "./TodaysTicketsDataTable";

const AdminTodaysTickets = () => {
  const { teamsOptions, dayOfRequestStatusOptions } = useDropdowns();
  const { loading, setLoadingNonBlocking } = useLoading();
  const { isMobile } = useBreakpoints();
  const [requests, setRequests] = useState<DayOfTableData[]>([]);
  const [displayedRequests, setDisplayedRequests] = useState<DayOfTableData[]>([]);
  const [toBeDeleted, setToBeDeleted] = useState<DayOfTableData | undefined>(undefined);

  const onDeleteClicked = (_index: number, value: DayOfTableData) => {
    setToBeDeleted(value);
  };

  const [{ startDate, endDate, teamId, statusId }, setParam] = useUrlParams({
    startDate: todayStr,
    endDate: nextMonthStr,
    teamId: undefined,
    statusId: undefined,
  });

  const dateRange: DateRange<Date> = {
    start: new Date(Date.parse(startDate!)),
    end: new Date(Date.parse(endDate!)),
  };

  const selectedHomeTeam = useMemo(() => getTeamOption(teamId, teamsOptions), [teamId, teamsOptions]);
  const selectedStatusOption = useMemo(
    () =>
      statusId
        ? dayOfRequestStatusOptions.find((val) => val.value.dayOfRequestStatusId === parseInt(statusId))
        : undefined,
    [statusId, dayOfRequestStatusOptions],
  );

  const getDayOfGamesRequestsCall = useCallback(async () => {
    try {
      setLoadingNonBlocking(true);
      const res: DayOfGameRequestDTO[] = await getDayOfRequests(startDate!, endDate!, teamId ? Number(teamId) : 0);
      const data: DayOfTableData[] = res.map((req: DayOfGameRequestDTO) => ({
        dayOfRequestId: req.dayOfRequestId,
        editLink: urlReplace(RouteConstants.ADMIN.TODAYS_TICKETS_EDIT, { requestId: req.dayOfRequestId }),
        firstName: req.user.firstName,
        lastName: req.user.lastName,
        ballparkEmail: req.emailAddress,
        numberOfTickets: req.numberOfTickets,
        game: req.game,
        ticketsSentDisplay: req.ticketSentDisplay,
        status: req.status,
        requestTime: formatSubmittedDate(req.requestedDate),
        statusId: req.statusId,
      }));
      setRequests(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingNonBlocking(false);
    }
  }, [startDate, endDate, teamId, setLoadingNonBlocking]);

  const updateDate = useCallback(
    (dateRange: DateRange<Date>) => {
      setParam({
        startDate: formatDate(dateRange.start, DATE_FORMATTER_STR),
        endDate: formatDate(dateRange.end, DATE_FORMATTER_STR),
      });
    },
    [setParam],
  );

  const deleteRequest = async (toBeDeleted: DayOfTableData) => {
    try {
      setLoadingNonBlocking(true);
      await deleteDayOfTicketRequest(toBeDeleted.dayOfRequestId);
      setToBeDeleted(undefined);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingNonBlocking(false);
    }
  };

  useEffect(() => {
    if (statusId) {
      setDisplayedRequests(requests.filter((req: DayOfTableData) => req.statusId === parseInt(statusId)));
    } else {
      setDisplayedRequests(requests);
    }
  }, [requests, statusId]);

  useEffect(() => {
    void getDayOfGamesRequestsCall();
  }, [getDayOfGamesRequestsCall]);

  return (
    <div>
      {!!toBeDeleted && (
        <ConfirmationModal
          show={!!toBeDeleted}
          onCancel={() => setToBeDeleted(undefined)}
          onConfirm={() => {
            void deleteRequest(toBeDeleted);
          }}
          header="Delete Today's Ticket Request"
          body={
            <Typography>
              <div className="bcr-mb-2">
                Are you sure you want to delete the request for {toBeDeleted.firstName} {toBeDeleted.lastName} for the
                game
              </div>
              <GameInfo
                gameDate={toBeDeleted.game.gameDate}
                venue={toBeDeleted.game.venue}
                away={toBeDeleted.game.teams.away.team}
                home={toBeDeleted.game.teams.home.team}
                seriesDescription={toBeDeleted.game.seriesDescription}
              />
            </Typography>
          }
          loading={loading}
        />
      )}
      <Header title="Today's Tickets Requests" flexColumn>
        <Filters actionIconPosition="absolute" numberDisplayed={isMobile ? 1 : 3}>
          <DateRangeInput
            label="Date Range"
            id="todays-tickets-date-range"
            value={dateRange}
            onChange={(value: DateRange<any> | NotApplicable) => {
              updateDate(value as DateRange<Date>);
            }}
            maxRange={{
              months: 1,
            }}
            withPortal
            popoverPlacement="bottom-end"
          />
          <Select
            id="hometeam-dropdown"
            label="Home Team"
            options={teamsOptions}
            value={selectedHomeTeam}
            onChange={(team: ValueOpt<StatsTeam>) => setParam("teamId", String(team.value.id))}
          />
          <Select
            id="RequestStatus"
            label="Request Status"
            options={dayOfRequestStatusOptions}
            value={selectedStatusOption}
            onChange={(value: ValueOpt<DayOfRequestStatus>) =>
              setParam(
                "statusId",
                value?.value?.dayOfRequestStatusId ? String(value.value.dayOfRequestStatusId) : undefined,
              )
            }
            clearable={true}
          />
        </Filters>
      </Header>
      <div className="bcr-p-2">
        <TodaysTicketsDataTable
          requests={displayedRequests}
          hasDelete
          hasEdit
          onDeleteClicked={onDeleteClicked}
          loading={loading}
        />
        <AdminActiveTodaysTicketsTable />
      </div>
    </div>
  );
};

export default AdminTodaysTickets;
